import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AvisarmeCuandoSalga } from '@renderer/components/publico/AvisarmeCuandoSalga';
import { Check, ChefHat, Clock, Copy, Bike, Loader2, PartyPopper } from 'lucide-react';

/**
 * Sprint 96 — La página del pedido del cliente, que no muere al mandarlo.
 *
 * Reporte del negocio con el sistema ya en el aire:
 *
 * > *"Una vez que el cliente envía su pedido, la página caduca, y el cliente se queda sin saber el
 * > monto final a pagar (con el envío) y además tampoco sabe a dónde va a transferir."*
 *
 * Era un hueco de diseño: el paso final decía *"te escribimos por WhatsApp con el total"*. Eso deja
 * ciego al cliente **y le devuelve el trabajo al negocio** —teclear total, envío y CLABE a mano—,
 * que es justo lo que el enlace venía a quitar.
 *
 * Ahora el cliente se queda con su página y ésta va cambiando sola: cuando el negocio cotiza el
 * envío, aparece el total exacto y los datos para depositar, **sin que nadie escriba un número**.
 *
 * ## Dos decisiones que se notan poco y valen
 *
 * - **Los datos del banco sólo salen cuando toca pagar.** Antes no le sirven, y después invitan a
 *   transferir dos veces.
 * - **Se refresca sola cada 20 segundos** mientras el pedido está vivo. Sin la API de WhatsApp no
 *   le podemos avisar, pero si dejó la página abierta —que es lo que hace quien está esperando su
 *   comida—, se entera solo.
 */

interface PedidoPublico {
  numeroPedido: number;
  estado: 'esperando_total' | 'listo_para_pagar' | 'pagado_en_cocina' | 'en_camino' | 'entregado' | 'cancelado';
  clienteNombre: string | null;
  monedaSimbolo: string;
  items: Array<{ nombre: string; cantidad: number; importe: number }>;
  comida: number;
  costoEnvio: number;
  totalAPagar: number | null;
  envioContraEntrega: boolean;
  datosParaPagar: string | null;
  nombreRepartidor: string | null;
  yaConfirmoRecibido: boolean;
  /** Sprint 101 — Ya avisó que transfirió. No significa pagado: eso lo confirma el negocio. */
  yaAvisoPago: boolean;
}

const money = (c: number, s = '$') =>
  `${s}${(c / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function PedidoPublicoPage() {
  const { token = '' } = useParams();
  const [pedido, setPedido] = useState<PedidoPublico | null>(null);
  const [cargando, setCargando] = useState(true);
  const [noExiste, setNoExiste] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [avisando, setAvisando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`/api/publico/pedido/${encodeURIComponent(token)}`);
      if (r.status === 404) {
        setNoExiste(true);
        return;
      }
      const d = await r.json();
      if (d.ok) setPedido(d.pedido);
      else setNoExiste(true);
    } catch {
      /* sin red: se reintenta en el siguiente ciclo */
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Mientras el pedido esté vivo, la página se entera sola de que ya hay total. */
  useEffect(() => {
    if (!pedido || pedido.estado === 'entregado' || pedido.estado === 'cancelado') return;
    const t = setInterval(() => void cargar(), 20000);
    return () => clearInterval(t);
  }, [pedido, cargar]);

  /**
   * Sprint 101 — "Ya hice la transferencia".
   *
   * Avisa y ya. No paga el pedido ni lo manda a cocina —eso lo decide una persona mirando su
   * cuenta—, pero saca al cliente del limbo en el que estaba: transfería y la página seguía
   * diciéndole que avisara por WhatsApp.
   */
  const avisarPago = async () => {
    setAvisando(true);
    await fetch(`/api/publico/pedido/${encodeURIComponent(token)}/aviso-pago`, { method: 'POST' });
    await cargar();
    setAvisando(false);
  };

  const confirmarRecibido = async () => {
    setConfirmando(true);
    await fetch(`/api/publico/pedido/${encodeURIComponent(token)}/recibido`, { method: 'POST' });
    await cargar();
    setConfirmando(false);
  };

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (noExiste || !pedido) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-center">
        <div>
          <div className="text-5xl">🔎</div>
          <h1 className="mt-3 text-xl font-bold">No encontramos ese pedido</h1>
          <p className="mt-1 text-sm text-slate-600">
            Revisa el enlace, o escríbenos por WhatsApp y con gusto te ayudamos.
          </p>
        </div>
      </div>
    );
  }

  const s = pedido.monedaSimbolo;
  const folio = String(pedido.numeroPedido).padStart(5, '0');

  return (
    /* Sprint 111 — `pb-32`: la barra de acción fija no puede tapar el último renglón del pedido. */
    <div className="mx-auto min-h-screen max-w-md bg-white px-4 pb-32 pt-6">
      <div className="text-center">
        <div className="text-xs uppercase tracking-wide text-slate-500">Tu pedido</div>
        <div className="font-mono text-2xl font-extrabold">#{folio}</div>
        {pedido.clienteNombre && (
          <div className="text-sm text-slate-600">{pedido.clienteNombre}</div>
        )}
      </div>

      <Paso estado={pedido.estado} />

      {/* Sprint 163 — El aviso "va en camino" al celular, si el negocio lo tiene encendido. */}
      <AvisarmeCuandoSalga token={token} yaSalio={['en_camino', 'entregado', 'cancelado'].includes(pedido.estado)} />

      {/* ── Lo que pidió ── */}
      <ul className="mt-5">
        {pedido.items.map((i, k) => (
          <li
            key={k}
            className="flex justify-between gap-3 border-b border-dashed border-slate-200 py-2.5 text-sm"
          >
            <span>
              <b className="text-primary">{i.cantidad}</b> {i.nombre}
            </span>
            <span className="font-mono">{money(i.importe, s)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex justify-between text-sm">
        <span>Comida</span>
        <span className="font-mono">{money(pedido.comida, s)}</span>
      </div>

      {/* ── El envío ── */}
      {pedido.totalAPagar === null ? (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="flex items-center gap-2 font-bold">
            <Clock className="h-4 w-4" />
            Estamos cotizando tu envío
          </div>
          <p className="mt-1 text-xs">
            En cuanto sepamos cuánto cuesta llevarlo, aquí mismo aparece tu total y los datos para
            depositar. <b>Puedes dejar esta página abierta.</b>
          </p>
        </div>
      ) : (
        <>
          <div className="mt-1 flex justify-between text-sm">
            <span>Envío</span>
            <span className="font-mono">
              {pedido.costoEnvio > 0 ? money(pedido.costoEnvio, s) : 'Cortesía'}
              {pedido.envioContraEntrega && pedido.costoEnvio > 0 && (
                <span className="ml-1 text-xs text-slate-500">(le pagas al repartidor)</span>
              )}
            </span>
          </div>

          <div className="mt-3 rounded-xl bg-slate-50 p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-bold">
                {pedido.estado === 'listo_para_pagar' ? 'Deposita exactamente' : 'Total'}
              </span>
              <span className="font-mono text-2xl font-extrabold text-primary">
                {money(pedido.totalAPagar, s)}
              </span>
            </div>
            {/*
              Los centavos no son un capricho: son lo que hace que el depósito se reconozca solo en
              la notificación del banco. Decírselo al cliente evita que crea que es un error.
            */}
            {pedido.estado === 'listo_para_pagar' && pedido.totalAPagar % 100 !== 0 && (
              <p className="mt-1 text-xs text-slate-600">
                Deposita el monto <b>completo, con los centavos</b>: así reconocemos tu pago al
                instante y tu pedido se va a la parrilla más rápido.
              </p>
            )}
          </div>
        </>
      )}

      {/* ── Cómo pagar ── */}
      {pedido.estado === 'listo_para_pagar' && pedido.datosParaPagar && (
        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-blue-900">
            Transfiere a
          </div>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-sm text-blue-900">
            {pedido.datosParaPagar}
          </pre>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(pedido.datosParaPagar ?? '');
                setCopiado(true);
              } catch {
                /* sin permiso: los datos están a la vista para copiarlos a mano */
              }
            }}
            className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 font-bold text-white"
          >
            <Copy className="h-4 w-4" />
            {copiado ? '✓ Copiado' : 'Copiar los datos'}
          </button>
          {/*
            Sprint 101 — El hueco que encontró la dueña probando el flujo.

            Aquí terminaba todo: "avísanos por WhatsApp". El cliente transfería y tenía que salirse
            de la página a otra app — y del otro lado, quien atiende no sabía a quién buscarle el
            depósito entre ciento cincuenta iguales.

            El botón **no paga nada**: deja dicho a qué hora asegura haberlo hecho, y eso ordena la
            cola de verificación del negocio. Por eso el texto de después no promete que ya está
            pagado, sino que alguien lo va a revisar: prometer de más aquí es como se pierde la
            confianza en un sistema que maneja dinero.
          */}
          {pedido.yaAvisoPago ? (
            <div className="mt-3 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-center">
              <div className="font-bold text-emerald-900">✓ Nos avisaste de tu depósito</div>
              <p className="mt-1 text-xs text-emerald-800">
                Lo están verificando en la cuenta. En cuanto lo vean, tu pedido se va a la parrilla
                y esta página se actualiza sola.
              </p>
            </div>
          ) : (
            <p className="mt-3 text-xs text-blue-800">
              Cuando la hayas hecho, apriétale abajo a <b>&quot;Ya hice la transferencia&quot;</b> y
              le avisamos al negocio. Lo verifican en su cuenta y de ahí se va a la parrilla.
            </p>
          )}
        </div>
      )}

      {pedido.yaConfirmoRecibido && (
        <div className="mt-5 rounded-xl bg-emerald-50 p-4 text-center">
          <PartyPopper className="mx-auto h-7 w-7 text-emerald-600" />
          <div className="mt-1 font-bold text-emerald-900">¡Gracias por tu compra!</div>
          <p className="text-xs text-emerald-800">Que lo disfrutes.</p>
        </div>
      )}

      <p className="mt-8 text-center text-xs text-slate-400">
        Guarda este enlace: aquí puedes ver cómo va tu pedido.
      </p>
      {/* Sprint 167 — El aviso de privacidad, también desde aquí: es la página que el cliente guarda. */}
      <a
        href="#/privacidad"
        className="mx-auto mt-2 flex min-h-[44px] w-fit items-center text-sm font-semibold text-slate-700 underline"
      >
        Aviso de privacidad
      </a>

      {/*
        Sprint 111 — La acción que le toca al cliente, siempre a la vista.

        ## La evidencia, no la opinión

        Reporte del dueño tras la prueba con cinco personas: *"al decirle no salgas de la página,
        los 5 betatester no hacen ni scroll por miedo, y no saben que podría haber acciones
        pendientes por hacer"*. Cinco de cinco. Los botones vivían al final del documento —después
        de los renglones, el total y los datos bancarios—, así que en un celular con ocho productos
        quedaban fuera de la pantalla y **nadie bajó a buscarlos**.

        En el celular de un cliente, lo que no se ve no existe. Y esta gente no es el operador: no
        va a explorar, no va a aprender la pantalla, la va a usar una vez.

        ## Tres reglas

        1. **Una sola acción.** Cinco personas que no se atreven a scrollear tampoco se atreven a
           elegir entre tres botones.
        2. **Si no hay nada que hacer, la barra dice qué está pasando.** El silencio fue lo que los
           asustó; un espacio en blanco se lee como "algo se rompió".
        3. Mismo patrón visual que la barra de la canasta en `MenuPublicoPage`, para que quien
           llega aquí desde allá no aprenda dos pantallas.
      */}
      <div className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-md border-t-2 border-slate-200 bg-white p-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        {pedido.estado === 'listo_para_pagar' && !pedido.yaAvisoPago ? (
          <button
            onClick={() => void avisarPago()}
            disabled={avisando}
            className="min-h-[56px] w-full rounded-xl bg-emerald-600 text-lg font-extrabold text-white disabled:opacity-50"
          >
            {avisando ? 'Un momento…' : 'Ya hice la transferencia'}
          </button>
        ) : pedido.estado === 'en_camino' && !pedido.yaConfirmoRecibido ? (
          <button
            onClick={() => void confirmarRecibido()}
            disabled={confirmando}
            className="min-h-[56px] w-full rounded-xl bg-emerald-600 text-lg font-extrabold text-white disabled:opacity-50"
          >
            {confirmando ? 'Un momento…' : '✓ Ya lo recibí'}
          </button>
        ) : (
          <div className="py-2 text-center text-sm font-semibold text-slate-600">
            {textoDeEspera(pedido)}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Sprint 111 — Qué decirle cuando no le toca hacer nada.
 *
 * Una barra vacía se lee como una pantalla rota. Cada frase está en presente y en su idioma —"lo
 * están preparando", no "estado: pagado_en_cocina"— y ninguna promete algo que el negocio todavía
 * no hizo: mientras el depósito no se verifica **no está pagado**, y decirlo de más aquí es como
 * se pierde la confianza en un sistema que maneja dinero.
 */
function textoDeEspera(pedido: PedidoPublico): string {
  if (pedido.estado === 'cancelado') return 'Este pedido fue cancelado.';
  if (pedido.yaConfirmoRecibido || pedido.estado === 'entregado') {
    return '¡Gracias por tu compra!';
  }
  if (pedido.estado === 'esperando_total') return '⏳ Estamos cotizando tu envío…';
  if (pedido.estado === 'listo_para_pagar') return '⏳ Verificando tu depósito…';
  if (pedido.estado === 'pagado_en_cocina') return '🔥 Están preparando tu pedido';
  if (pedido.estado === 'en_camino') return '🏍️ Tu pedido va en camino';
  return 'Aquí puedes ver cómo va tu pedido.';
}

/** La línea de tiempo, en palabras del cliente y no del sistema. */
function Paso({ estado }: { estado: PedidoPublico['estado'] }) {
  if (estado === 'cancelado') {
    return (
      <div className="mt-5 rounded-xl bg-rose-50 p-4 text-center text-sm text-rose-800">
        Este pedido se canceló. Si crees que es un error, escríbenos por WhatsApp.
      </div>
    );
  }

  const pasos = [
    { clave: 'esperando_total', texto: 'Recibimos tu pedido', icono: <Check className="h-4 w-4" /> },
    { clave: 'listo_para_pagar', texto: 'Ya tienes tu total', icono: <Check className="h-4 w-4" /> },
    { clave: 'pagado_en_cocina', texto: 'En la parrilla', icono: <ChefHat className="h-4 w-4" /> },
    { clave: 'en_camino', texto: 'Va en camino', icono: <Bike className="h-4 w-4" /> },
    { clave: 'entregado', texto: 'Entregado', icono: <PartyPopper className="h-4 w-4" /> },
  ] as const;

  const actual = pasos.findIndex((p) => p.clave === estado);

  return (
    <ol className="mt-5 space-y-2">
      {pasos.map((p, i) => {
        const hecho = i <= actual;
        return (
          <li
            key={p.clave}
            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm ${
              i === actual
                ? 'bg-primary/10 font-bold text-primary'
                : hecho
                  ? 'text-emerald-700'
                  : 'text-slate-400'
            }`}
          >
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full ${
                i === actual ? 'bg-primary text-white' : hecho ? 'bg-emerald-100' : 'bg-slate-100'
              }`}
            >
              {p.icono}
            </span>
            {p.texto}
          </li>
        );
      })}
    </ol>
  );
}
